import {
  Box,
  IconButton,
  Input,
  InputAdornment,
  Link,
  Tooltip,
  Typography,
} from "@mui/material";
import { useModeStoreEntries } from "../../hooks/useModeStoreEntries";
import {
  DataGridPremium,
  GridActionsCellItem,
  GridColDef,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { useDataGridPremium, gridCellExpandRenderer } from "@myplant-io/mui";
import { useCallback, useMemo, useState } from "react";
import { Mode, ModeStoreEntry } from "../../types";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { useModeStoreEntryDelete } from "../../hooks/useModeStoreEntryDelete";
import { useQueryClient } from "react-query";
import { useModeStoreEntrySubscribe } from "../../hooks/useModeStoreEntrySubscribe";
import { useModes } from "../../hooks/useModes";
import { ModeUpdateDialog } from "../../components/common/ModeUpdateDialog";
import { ModeStoreUpdateDialog } from "../../components/common/ModeStoreUpdateDialog";
import { useModeStoreEntryEdit } from "../../hooks/useModeStoreEntryEdit";
import { useSnackbar } from "@myplant-io/snackbar";
import { useNavigate } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";
import { RouteNames } from "../../RouteNames";
import { commonMessages } from "../../commonMessages";
import { useAppState } from "../../state/App";
import { useDebouncedPickerQuery } from "../../hooks/useDebouncedPickerQuery";

const GRID_STATE_ID = "MODE_STORE_GRID_STATE";

const messages = defineMessages({
  subscribe: {
    id: "modeStore.subscribe",
    defaultMessage: "Subscribe",
  },
  unsubscribe: {
    id: "modeStore.unsubscribe",
    defaultMessage: "Unsubscribe",
  },
  subscribeSuccessMessage: {
    id: "modeStore.subscribeSuccessMessage",
    defaultMessage: "Successfully installed agent",
  },
  subscribeSuccessAction: {
    id: "modeStore.subscribeSuccessAction",
    defaultMessage: "Go to chat",
  },
  unsubscribeSuccessMessage: {
    id: "modeStore.unsubscribeSuccessMessage",
    defaultMessage: "Successfully uninstalled agent",
  },
  unsubscribeSuccessAction: {
    id: "modeStore.unsubscribeSuccessAction",
    defaultMessage: "Go to chat",
  },
  mode: {
    id: "modeStore.column.mode",
    defaultMessage: "Agent",
  },
  description: {
    id: "modeStore.column.description",
    defaultMessage: "Description",
  },
  publishedBy: {
    id: "modeStore.column.publishedBy",
    defaultMessage: "Published By",
  },
  publishedAt: {
    id: "modeStore.column.publishedAt",
    defaultMessage: "Published At",
  },
  usersCount: {
    id: "modeStore.column.usersCount",
    defaultMessage: "# Users",
  },
  actions: {
    id: "modeStore.column.actions",
    defaultMessage: "Actions",
  },
});

export function ModeStore() {
  const { formatMessage: t } = useIntl();
  const {
    actions: { setModeId },
  } = useAppState();
  const queryClient = useQueryClient();
  const { showSuccessMessage } = useSnackbar();
  const navigate = useNavigate();
  const { data: modeStoreEntries, isLoading } = useModeStoreEntries();
  const { data: modes } = useModes();
  const { mutateAsync: deleteModeStoreEntry } = useModeStoreEntryDelete();
  const { mutateAsync: subscribeModeStoreEntry } = useModeStoreEntrySubscribe();
  const { mutateAsync: editModeStoreEntry } = useModeStoreEntryEdit();
  // check if description column is resized by the user
  const [isResized, setIsResized] = useState(
    JSON.parse(localStorage.getItem(`${GRID_STATE_ID}_premium`) ?? "{}")?.state
      ?.columns?.dimensions?.description?.width > 1
  );
  const [modeStoreEntry, setModeStoreEntry] = useState<ModeStoreEntry>();
  const [previewMode, setPreviewMode] = useState<Mode | undefined>();
  const searchQuery = useDebouncedPickerQuery();

  const canSubscribe = useCallback(
    (modeId: string) => {
      if (!modes) {
        return false;
      }
      const mode = modes.find((mode) => mode._id === modeId);
      return !mode;
    },
    [modes]
  );

  const columns: GridColDef<ModeStoreEntry>[] = useMemo(
    () => [
      {
        field: "mode",
        headerName: t(messages.mode),
        width: 300,
        valueGetter: (_, row) => row.mode.name,
        renderCell: (params) => {
          const cell = (
            <Box>
              <Typography variant="body2">{params.value}</Typography>
            </Box>
          );

          if (!params.row.subscribed) {
            return cell;
          }

          return (
            <Link
              href="/chat"
              onClick={(e) => {
                e.preventDefault();
                setModeId(params.row.mode._id);
                navigate(RouteNames.CHAT);
              }}
            >
              {cell}
            </Link>
          );
        },
      },
      {
        field: "description",
        headerName: t(messages.description),
        flex: isResized ? 0 : 1,
        renderCell: gridCellExpandRenderer,
      },
      {
        field: "publishedByUsername",
        headerName: t(messages.publishedBy),
        width: 450,
        valueGetter: (_, row) => {
          return `${row.published_by.full_name} (${row.published_by.email})`;
        },
        renderCell: ({ value }) => {
          return (
            <Box>
              <Typography variant="body2">{value}</Typography>
            </Box>
          );
        },
      },
      {
        field: "published_at",
        headerName: t(messages.publishedAt),
        type: "dateTime",
        width: 200,
        valueGetter: (value) => (value ? new Date(value) : null),
      },
      {
        field: "users_count",
        headerName: t(messages.usersCount),
        type: "number",
        valueGetter: (_, row) => {
          return row.user_count || 0;
        },
      },
      {
        field: "actions",
        headerName: t(messages.actions),
        width: 150,
        type: "actions",
        getActions: (params) => [
          ...(canSubscribe(params.row.mode._id)
            ? [
                <Tooltip key="add" title={t(messages.subscribe)}>
                  <GridActionsCellItem
                    icon={<AddShoppingCartIcon />}
                    onClick={async () => {
                      await subscribeModeStoreEntry({
                        mode_id: params.row.mode_id,
                      });
                      showSuccessMessage({
                        snackbarProps: {
                          autoHideDuration: 3000,
                        },
                        message: t(messages.subscribeSuccessMessage),
                        action: t(messages.subscribeSuccessAction),
                        handleAction: () => navigate(RouteNames.CHAT),
                      });
                    }}
                    label={t(messages.subscribe)}
                  />
                </Tooltip>,
              ]
            : []),
          ...(!params.row.mode.read_only
            ? [
                <Tooltip key="edit" title={t(commonMessages.edit)}>
                  <GridActionsCellItem
                    icon={<Edit />}
                    onClick={async () => {
                      setModeStoreEntry(params.row);
                    }}
                    label={t(commonMessages.edit)}
                  />
                </Tooltip>,
                <Tooltip key="delete" title={t(commonMessages.unpublish)}>
                  <GridActionsCellItem
                    icon={<Delete />}
                    onClick={async () => {
                      await deleteModeStoreEntry({
                        mode_id: params.row._id,
                      });
                      queryClient.invalidateQueries("modes");
                      queryClient.invalidateQueries("mode-store");
                    }}
                    label={t(commonMessages.delete)}
                  />
                </Tooltip>,
              ]
            : []),
        ],
      },
    ],
    [
      canSubscribe,
      deleteModeStoreEntry,
      isResized,
      navigate,
      queryClient,
      setModeId,
      showSuccessMessage,
      subscribeModeStoreEntry,
      t,
    ]
  );

  const apiRef = useGridApiRef();
  const { gridProps, onRestore } = useDataGridPremium({
    apiRef,
    gridStateId: "MODE_STORE_GRID_STATE",
    columns,
    autoPageSize: true,
    slotProps: {
      toolbar: {
        onRestore: () => {
          setIsResized(false);
          onRestore();
        },
      },
    },
    initialState: {
      sorting: {
        sortModel: [
          {
            field: "users_count",
            sort: "desc",
          },
          {
            field: "mode",
            sort: "asc",
          },
        ],
      },
    },
  });

  const rows = useMemo(() => {
    if (!modeStoreEntries) {
      return [];
    }
    return modeStoreEntries
      .filter(({ mode, description }) => {
        const s = searchQuery.query.toLowerCase();
        return (
          mode.name.toLowerCase().includes(s) ||
          description.toLowerCase().includes(s)
        );
      })
      .map((entry) => ({
        ...entry,
        publishedByUsername: entry.published_by?.username || "",
        publishedByEmail: entry.published_by?.email || "",
      }));
  }, [modeStoreEntries, searchQuery.query]);

  const handleModeStoreEntryClose = () => {
    setModeStoreEntry(undefined);
  };

  return (
    <>
      <div className="p-4 sm:px-6 py-4">
        <Typography variant="h4">Agent Store</Typography>
      </div>

      <div className="flex flex-col flex-grow p-4 sm:px-6 py-4">
        <div className="flex flex-col flex-grow">
          <div className="flex justify-end my-4">
            <Input
              data-testid="mode-store-search"
              id="search"
              placeholder={t(commonMessages.search)}
              value={searchQuery.inputValue}
              onChange={(e) => searchQuery.setQuery(e.target.value)}
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              }
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    disabled={searchQuery.inputValue !== "" ? false : true}
                    aria-label="clear search"
                    onClick={() => searchQuery.setQuery("")}
                    size="large"
                  >
                    <CloseIcon />
                  </IconButton>
                </InputAdornment>
              }
            />
          </div>
          <DataGridPremium<ModeStoreEntry>
            {...gridProps}
            loading={isLoading}
            onColumnResize={(params) => {
              if (params.colDef.field === "description") {
                setIsResized(true);
              }
            }}
            rows={rows}
            getRowId={(row) => row._id}
            pagination
            disableRowSelectionOnClick
            sx={{
              "& .MuiDataGrid-row:hover": {
                cursor: "pointer",
              },
            }}
            onRowClick={(args) => setPreviewMode(args.row.mode)}
          />
        </div>
      </div>
      <ModeUpdateDialog
        open={Boolean(previewMode)}
        onClose={() => setPreviewMode(undefined)}
        mode={previewMode}
        readOnly={true}
      />
      <ModeStoreUpdateDialog
        mode={modeStoreEntry?.mode}
        modeStoreEntry={modeStoreEntry}
        open={!!modeStoreEntry}
        onClose={() => {
          handleModeStoreEntryClose();
        }}
        onSubmit={async (data) => {
          if (!modeStoreEntry) {
            return;
          }
          await editModeStoreEntry({
            id: modeStoreEntry?._id,
            mode_id: data.modeId,
            description: data.description,
          });
          handleModeStoreEntryClose();
        }}
      />
    </>
  );
}
